import React,{useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {accountPlans} from '../data/accountPlans';
import './LandingPage.css';
import './BillingScreen.css';

const {
  FiCheck,
  FiCreditCard,
  FiTrendingUp,
  FiX
}=FiIcons;

function UsageMeter({label,value,limit}) {
  const unlimited=limit===null;
  const percentage=unlimited ? 0 : Math.min((value/limit)*100,100);
  const overLimit=!unlimited && value>limit;

  return (
    <div className="billing-usage-item">
      <div className="billing-usage-label">
        <span>{label}</span>
        <strong>
          {unlimited ? `${value} used · Unlimited` : `${value} of ${limit}`}
        </strong>
      </div>
      <div
        className={`billing-progress ${overLimit ? 'over-limit' : ''}`}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin="0"
        aria-valuemax={limit || undefined}
      >
        <i style={{width:`${percentage}%`}} />
      </div>
      {overLimit && (
        <small>
          Current usage is above this plan’s included limit.
        </small>
      )}
    </div>
  );
}

function PlanOption({plan,currentPlan,onSelect}) {
  const active=plan.id===currentPlan.id;

  return (
    <article
      className={`pricing-card ${plan.recommended ? 'recommended' : ''}`}
    >
      {plan.recommended && (
        <span className="recommended-label">Most popular</span>
      )}
      <h3>{plan.name}</h3>
      <div className="plan-price">
        {plan.price}<small>/mo</small>
      </div>
      <p className="plan-description">{plan.description}</p>
      <ul>
        {plan.features.map((feature)=> (
          <li key={feature}>
            <SafeIcon icon={FiCheck} />
            {feature}
          </li>
        ))}
      </ul>
      <button
        type="button"
        className={plan.recommended ? 'landing-primary' : 'plan-button'}
        style={plan.recommended ? {background:'#19382f'} : undefined}
        disabled={active}
        onClick={()=> onSelect(plan)}
      >
        {active ? 'Current plan' : 'Switch to this plan'}
      </button>
    </article>
  );
}

function BillingScreen({
  currentPlan,
  brandCount,
  postCount,
  onChangePlan
}) {
  const [choosingPlan,setChoosingPlan]=useState(false);
  const [paymentNotice,setPaymentNotice]=useState(false);

  const choosePlan=(plan)=> {
    onChangePlan(plan);
    setChoosingPlan(false);
  };

  return (
    <motion.main
      className="page billing-page"
      initial={{opacity:0}}
      animate={{opacity:1}}
    >
      <header className="billing-header">
        <div>
          <span className="eyebrow">Account workspace</span>
          <h1>Billing & plan</h1>
          <p>See what your workspace includes and how much you’re using.</p>
        </div>
        <span className="billing-header-mark">
          <SafeIcon icon={FiCreditCard} />
        </span>
      </header>

      <section className="billing-overview">
        <div className="billing-plan-summary">
          <span className="eyebrow">Current plan</span>
          <div className="billing-plan-title">
            <h2>{currentPlan.name}</h2>
            <p>{currentPlan.price}<small>/month</small></p>
          </div>
          <p>{currentPlan.description}</p>
          <ul>
            {currentPlan.features.map((feature)=> (
              <li key={feature}>
                <SafeIcon icon={FiCheck} />
                {feature}
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="primary-button"
            onClick={()=> setChoosingPlan(true)}
          >
            <SafeIcon icon={FiTrendingUp} />
            Upgrade plan
          </button>
        </div>

        <div className="billing-usage-panel">
          <span className="eyebrow">This month</span>
          <h2>Workspace usage</h2>
          <p>
            Usage is estimated from the content and brands in this preview.
          </p>
          <div className="billing-usage-list">
            <UsageMeter
              label="Brands"
              value={brandCount}
              limit={currentPlan.brandLimit}
            />
            <UsageMeter
              label="Posts generated"
              value={postCount}
              limit={currentPlan.postLimit}
            />
          </div>
        </div>
      </section>

      <section className="billing-payment">
        <span className="billing-card-icon">
          <SafeIcon icon={FiCreditCard} />
        </span>
        <div>
          <span className="eyebrow">Payment method</span>
          <h2>Visa ending in 4242</h2>
          <p>Expires 08/28 · Mock payment details</p>
        </div>
        <button
          type="button"
          onClick={()=> setPaymentNotice(true)}
        >
          Update
        </button>
      </section>

      {paymentNotice && (
        <motion.p
          className="billing-placeholder-note"
          initial={{opacity:0,y:-5}}
          animate={{opacity:1,y:0}}
        >
          Payment method updates are unavailable in this frontend preview.
        </motion.p>
      )}

      <p className="billing-footnote">
        Billing is simulated. No payment is processed when plans are changed.
      </p>

      {choosingPlan && (
        <div
          className="modal-backdrop billing-plan-backdrop"
          onMouseDown={()=> setChoosingPlan(false)}
        >
          <motion.section
            className="billing-plan-modal"
            onMouseDown={(event)=> event.stopPropagation()}
            initial={{opacity:0,scale:0.97,y:12}}
            animate={{opacity:1,scale:1,y:0}}
          >
            <button
              type="button"
              className="billing-modal-close"
              aria-label="Close plan options"
              onClick={()=> setChoosingPlan(false)}
            >
              <SafeIcon icon={FiX} />
            </button>
            <div className="billing-modal-heading">
              <span className="eyebrow">Choose a plan</span>
              <h2>Find the right room to grow.</h2>
              <p>Switching plans updates this preview immediately.</p>
            </div>
            <div className="pricing-grid billing-pricing-grid">
              {accountPlans.map((plan)=> (
                <PlanOption
                  key={plan.id}
                  plan={plan}
                  currentPlan={currentPlan}
                  onSelect={choosePlan}
                />
              ))}
            </div>
          </motion.section>
        </div>
      )}
    </motion.main>
  );
}

export default BillingScreen;